import { useEffect } from "react";

import { isTest } from "../constants/env";
import {
  TURNSTILE_CHALLENGE_IN_FLIGHT_MAX_MS,
  TURNSTILE_CHALLENGE_INTERVAL_MS,
  TURNSTILE_CHALLENGE_JITTER_MS,
  TURNSTILE_ERROR_MAX_RETRIES,
  TURNSTILE_ERROR_RETRY_DELAY_MS,
  TURNSTILE_FORCED_REVERIFY_COOLDOWN_MS,
  TURNSTILE_MAX_CONSECUTIVE_ERRORS,
  TURNSTILE_SCRIPT_READY_POLL_MS,
  TURNSTILE_SCRIPT_READY_TIMEOUT_MS,
  TURNSTILE_SKIPPED_CHALLENGE_MIN_RETRY_MS,
  TURNSTILE_STALE_TOKEN_THRESHOLD_MS,
  TURNSTILE_VERIFY_RATE_LIMITED_RETRY_MS,
} from "../constants/turnstile";
import { trpc } from "../helpers/trpc";
import { setDeviceId } from "../helpers/deviceIdCredential";
import { hasSecurityExemption } from "../helpers/securityExemption";
import { decideReChallenge, ReChallengeOptions, setReChallengeListener } from "../helpers/reChallengeTrigger";
import { createRateLimitedBackoff, handleVerifyError, handleVerifyResult } from "../helpers/turnstileVerifyRateLimit";
import { getSessionTokenIssuedAtMs, hasFreshSessionToken, setSessionToken } from "../helpers/sessionTokenCredential";
import {
  getTurnstileWidgetErrorStreak,
  isVerificationExhausted,
  recordTurnstileFailure,
  recordTurnstileSuccess,
  recordTurnstileWidgetError,
} from "../helpers/turnstileHealth";
import { logAnomaly } from "../helpers/turnstileAnomalyLog";
import { reportTurnstileOutcome, TurnstileReportDetails } from "../helpers/turnstileReport";

export interface TurnstileRenderOptions {
  sitekey: string;
  appearance?: "always" | "execute" | "interaction-only";
  retry?: "auto" | "never";
  "refresh-expired"?: "auto" | "manual" | "never";
  "refresh-timeout"?: "auto" | "manual" | "never";
  callback?: (token: string) => void;
  "error-callback"?: (errorCode?: string) => void;
}

export interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const getRandomizedIntervalMs = (): number =>
  TURNSTILE_CHALLENGE_INTERVAL_MS + (Math.random() * 2 - 1) * TURNSTILE_CHALLENGE_JITTER_MS;

const getSharedTokenAgeMs = (): number | null => {
  const issuedAtMs = getSessionTokenIssuedAtMs();
  return issuedAtMs === null ? null : Date.now() - issuedAtMs;
};

const getSkippedChallengeRetryMs = (tokenAgeMs: number | null): number | null =>
  tokenAgeMs !== null && tokenAgeMs < TURNSTILE_CHALLENGE_INTERVAL_MS - TURNSTILE_CHALLENGE_JITTER_MS
    ? Math.max(getRandomizedIntervalMs() - tokenAgeMs, TURNSTILE_SKIPPED_CHALLENGE_MIN_RETRY_MS)
    : null;

const storeIssuedCredentials = (data: { sessionToken?: string | null; deviceToken?: string | null } | null): void => {
  if (data?.deviceToken) {
    setDeviceId(data.deviceToken);
  }
  if (data?.sessionToken) {
    setSessionToken(data.sessionToken);
  }
};

const getErrorPhase = (): NonNullable<TurnstileReportDetails["phase"]> =>
  hasFreshSessionToken() ? "renewal" : "first_load";

const createErrorRetryBudget = (resetWidget: () => void) => {
  let retryCount = 0;
  let isExhausted = false;

  const removeInteractionListeners = (): void => {
    document.removeEventListener("pointerdown", handleInteraction);
    document.removeEventListener("keydown", handleInteraction);
  };
  const reopen = (): void => {
    retryCount = 0;
    isExhausted = false;
    removeInteractionListeners();
  };
  const handleInteraction = (): void => {
    reopen();
    resetWidget();
  };

  return {
    reopen,
    consume: (phase: NonNullable<TurnstileReportDetails["phase"]>): boolean => {
      if (retryCount < TURNSTILE_ERROR_MAX_RETRIES) {
        retryCount += 1;
        return true;
      }
      if (!isExhausted) {
        isExhausted = true;
        logAnomaly("challenge_retries_exhausted", { phase });
        void reportTurnstileOutcome("retries_exhausted", undefined, undefined, { phase });
        document.addEventListener("pointerdown", handleInteraction, { once: true });
        document.addEventListener("keydown", handleInteraction, { once: true });
      }
      return false;
    },
    resumeOnVisible: (): boolean => {
      const wasExhausted = isExhausted;
      reopen();
      return wasExhausted;
    },
    dispose: removeInteractionListeners,
  };
};

const createChallengeTracker = () => {
  let startedAt: number | null = null;

  return {
    markStarted: (): void => {
      startedAt = Date.now();
    },
    markSettled: (): void => {
      startedAt = null;
    },
    inFlightAgeMs: (): number | null => {
      const ageMs = startedAt === null ? null : Date.now() - startedAt;
      return ageMs !== null && ageMs < TURNSTILE_CHALLENGE_IN_FLIGHT_MAX_MS ? ageMs : null;
    },
  };
};

const createCookieResetTracker = (onCookiesReset: () => void) => {
  let isDisposed = false;
  let isResetPending = false;
  let successCount = 0;

  return {
    reportWidgetError: (errorCode: string | undefined, phase: NonNullable<TurnstileReportDetails["phase"]>): void => {
      const successCountAtReport = successCount;
      void reportTurnstileOutcome("widget_error", errorCode, undefined, { phase }).then((report) => {
        if (report.reset && !isDisposed && successCount === successCountAtReport && !isVerificationExhausted()) {
          isResetPending = true;
          onCookiesReset();
        }
      });
    },
    handleSuccess: (): void => {
      successCount += 1;
      if (isResetPending) {
        isResetPending = false;
        void reportTurnstileOutcome("reset_recovered");
      }
    },
    dispose: (): void => {
      isDisposed = true;
    },
  };
};

const recordVerifyOutcome = (
  data: { sessionToken?: string | null; deviceToken?: string | null } | null,
  errorRetryBudget: { reopen: () => void },
  cookieResetTracker: { handleSuccess: () => void }
): void => {
  if (data?.sessionToken) {
    const widgetErrorStreak = getTurnstileWidgetErrorStreak();
    errorRetryBudget.reopen();
    recordTurnstileSuccess();
    cookieResetTracker.handleSuccess();
    if (widgetErrorStreak > 0) {
      void reportTurnstileOutcome("recovered_after_error", undefined, widgetErrorStreak);
    }
  } else {
    recordTurnstileFailure();
  }
  storeIssuedCredentials(data);
};

const createExhaustionReporter = () => {
  let hasReported = false;

  return (errorCode: string | undefined, phase: NonNullable<TurnstileReportDetails["phase"]>): void => {
    if (hasReported) {
      return;
    }
    hasReported = true;
    logAnomaly("verification_exhausted", { errorCode: errorCode ?? "unknown", phase });
    void reportTurnstileOutcome("verification_exhausted", errorCode, TURNSTILE_MAX_CONSECUTIVE_ERRORS, { phase });
  };
};

const createLeftUnverifiedReporter = () => {
  let hasReported = false;

  return (): void => {
    const widgetErrorStreak = getTurnstileWidgetErrorStreak();
    if (hasReported || isVerificationExhausted() || widgetErrorStreak === 0 || hasFreshSessionToken()) {
      return;
    }
    hasReported = true;
    void reportTurnstileOutcome("left_unverified", undefined, widgetErrorStreak);
  };
};

const handleWidgetError = (
  errorCode: string | undefined,
  deps: {
    challengeTracker: { markSettled: () => void };
    cookieResetTracker: {
      reportWidgetError: (errorCode: string | undefined, phase: NonNullable<TurnstileReportDetails["phase"]>) => void;
    };
    errorRetryBudget: { consume: (phase: NonNullable<TurnstileReportDetails["phase"]>) => boolean };
    reportExhaustion: (errorCode: string | undefined, phase: NonNullable<TurnstileReportDetails["phase"]>) => void;
    scheduleErrorRetry: () => void;
  }
): void => {
  deps.challengeTracker.markSettled();
  const phase = getErrorPhase();
  logAnomaly("challenge_error", { errorCode: errorCode ?? "unknown", phase });
  recordTurnstileWidgetError();
  deps.cookieResetTracker.reportWidgetError(errorCode, phase);
  if (isVerificationExhausted()) {
    deps.reportExhaustion(errorCode, phase);
    return;
  }
  if (deps.errorRetryBudget.consume(phase)) {
    deps.scheduleErrorRetry();
  }
};

const isTokenStale = (tokenAgeMs: number | null): boolean =>
  tokenAgeMs !== null && tokenAgeMs > TURNSTILE_STALE_TOKEN_THRESHOLD_MS;

export const useTurnstileChallenge = ({ enabled = true }: { enabled?: boolean } = {}): void => {
  const { mutateAsync: verifyChallenge } = trpc.verifyTurnstileChallenge.useMutation();

  useEffect(() => {
    if (!enabled || isTest || isVerificationExhausted() || hasSecurityExemption()) {
      return;
    }

    const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_KEY;
    if (!siteKey) {
      logAnomaly("site_key_missing");
      return;
    }

    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.top = "50%";
    container.style.left = "50%";
    container.style.transform = "translate(-50%, -50%)";
    container.style.zIndex = "99999999";
    document.body.appendChild(container);

    let widgetId: string | null = null;
    let errorRetryTimeoutId: number | undefined;
    let challengeTimeoutId: number | undefined;
    let remainingMs = getRandomizedIntervalMs();
    let armedAt = Date.now();
    let lastForcedReverifyAt = 0;
    const challengeTracker = createChallengeTracker();
    const rateLimitedBackoff = createRateLimitedBackoff();
    const clearChallengeTimeout = (): void => {
      window.clearTimeout(challengeTimeoutId);
      challengeTimeoutId = undefined;
    };

    const armChallengeTimeout = (): void => {
      clearChallengeTimeout();
      if (isVerificationExhausted()) {
        return;
      }
      armedAt = Date.now();
      challengeTimeoutId = window.setTimeout(handleScheduledChallenge, remainingMs);
    };

    const handleScheduledChallenge = (): void => {
      const skippedRetryMs = getSkippedChallengeRetryMs(getSharedTokenAgeMs());
      if (skippedRetryMs === null) {
        resetWidget();
        return;
      }
      remainingMs = skippedRetryMs;
      if (document.visibilityState === "visible") {
        armChallengeTimeout();
      }
    };

    const resetWidget = (): void => {
      window.clearTimeout(errorRetryTimeoutId);
      rateLimitedBackoff.clear();
      if (isVerificationExhausted()) {
        return;
      }
      challengeTracker.markStarted();
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.reset(widgetId);
        } catch (error) {
          logAnomaly("reset_threw", { error: String(error) });
        }
      }
      remainingMs = getRandomizedIntervalMs();
      if (document.visibilityState === "visible") {
        armChallengeTimeout();
      }
    };

    const startForcedChallenge = (): void => {
      lastForcedReverifyAt = Date.now();
      resetWidget();
    };

    const errorRetryBudget = createErrorRetryBudget(() => resetWidget());
    const reportExhaustion = createExhaustionReporter();
    const reportLeftUnverified = createLeftUnverifiedReporter();
    window.addEventListener("pagehide", reportLeftUnverified);

    const scheduleErrorRetry = (delayMs: number = TURNSTILE_ERROR_RETRY_DELAY_MS): void => {
      window.clearTimeout(errorRetryTimeoutId);
      errorRetryTimeoutId = window.setTimeout(resetWidget, Math.max(delayMs, rateLimitedBackoff.remainingMs()));
    };

    const cookieResetTracker = createCookieResetTracker(() => {
      errorRetryBudget.reopen();
      scheduleErrorRetry();
    });

    const scheduleRateLimitedRetry = (): void => {
      rateLimitedBackoff.start();
      scheduleErrorRetry(TURNSTILE_VERIFY_RATE_LIMITED_RETRY_MS);
    };

    const verifyHandlers = {
      onRateLimited: scheduleRateLimitedRetry,
      onOutcome: (data: { sessionToken?: string | null; deviceToken?: string | null } | null): void =>
        recordVerifyOutcome(data, errorRetryBudget, cookieResetTracker),
      onFailure: recordTurnstileFailure,
    };

    const renderWidget = (): void => {
      if (!window.turnstile) {
        return;
      }
      lastForcedReverifyAt = Date.now();
      challengeTracker.markStarted();
      try {
        widgetId = window.turnstile.render(container, {
          sitekey: siteKey,
          appearance: "interaction-only",
          retry: "never",
          "refresh-expired": "never",
          "refresh-timeout": "never",
          callback: (token) => {
            window.clearTimeout(errorRetryTimeoutId);
            rateLimitedBackoff.clear();
            void verifyChallenge({ token })
              .then((result) => handleVerifyResult(result, verifyHandlers))
              .catch((error: unknown) => handleVerifyError(error, verifyHandlers))
              .finally(() => {
                challengeTracker.markSettled();
              });
          },
          "error-callback": (errorCode) =>
            handleWidgetError(errorCode, {
              challengeTracker,
              cookieResetTracker,
              errorRetryBudget,
              reportExhaustion,
              scheduleErrorRetry,
            }),
        });
      } catch (error) {
        logAnomaly("render_threw", { error: String(error) });
      }
    };

    let readyPollId: number | undefined;
    let hasLoggedScriptNotLoaded = false;
    if (window.turnstile) {
      renderWidget();
    } else {
      const pollStartedAt = Date.now();
      readyPollId = window.setInterval(() => {
        if (window.turnstile) {
          window.clearInterval(readyPollId);
          renderWidget();
        } else if (!hasLoggedScriptNotLoaded && Date.now() - pollStartedAt > TURNSTILE_SCRIPT_READY_TIMEOUT_MS) {
          hasLoggedScriptNotLoaded = true;
          logAnomaly("script_not_loaded");
          void reportTurnstileOutcome("script_load_timeout");
        }
      }, TURNSTILE_SCRIPT_READY_POLL_MS);
    }

    if (document.visibilityState === "visible") {
      armChallengeTimeout();
    }

    const handleVisibilityChange = (): void => {
      if (document.visibilityState !== "visible") {
        remainingMs -= Date.now() - armedAt;
        clearChallengeTimeout();
        return;
      }

      if (errorRetryBudget.resumeOnVisible()) {
        startForcedChallenge();
        return;
      }

      if (isTokenStale(getSharedTokenAgeMs())) {
        startForcedChallenge();
        return;
      }

      if (remainingMs <= 0) {
        startForcedChallenge();
      } else {
        armChallengeTimeout();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    setReChallengeListener(({ reason }: ReChallengeOptions) => {
      const decision = decideReChallenge({
        reason,
        isInsideCooldown: Date.now() - lastForcedReverifyAt < TURNSTILE_FORCED_REVERIFY_COOLDOWN_MS,
        challengeInFlightAgeMs: challengeTracker.inFlightAgeMs(),
        isRateLimitedBackoff: rateLimitedBackoff.isActive(),
        isExhausted: isVerificationExhausted(),
      });
      if (decision === "skip") {
        return;
      }
      if (decision === "start_bypassing_cooldown") {
        logAnomaly("rechallenge_cooldown_bypassed", { reason });
      }
      startForcedChallenge();
    });

    return () => {
      cookieResetTracker.dispose();
      window.removeEventListener("pagehide", reportLeftUnverified);
      window.clearInterval(readyPollId);
      clearChallengeTimeout();
      window.clearTimeout(errorRetryTimeoutId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      errorRetryBudget.dispose();
      setReChallengeListener(null);
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch (error) {
          logAnomaly("remove_threw", { error: String(error) });
        }
      }
      container.remove();
    };
  }, [enabled, verifyChallenge]);
};
